import { useState, type ReactNode } from "react";

import DeliveryMessage from "./deliveryMessage";
import LogisticsTracking from "./logisticsTracking";
import PayHistory from "./payHistory";
import ProveMaterial from "./proveMaterial";
import type { TradeOrderData } from "../TradeOrderComponentPrimitives";
import type { DeliveryVo } from "@zhihaoscm/entity";

export interface DeliveryDetailProps {
    data?: TradeOrderData;
    payHistory?: TradeOrderData[];
    tracking?: TradeOrderData[];
    materials?: TradeOrderData[];
    showPayHistory?: boolean;
    headerContent?: ReactNode;
    onTabChange?: (tab: string) => void;
    onEdit?: (data: TradeOrderData) => void;
    onCancel?: (data: TradeOrderData) => void;
    onInitiate?: (data: TradeOrderData) => void;
    onDelete?: (data: TradeOrderData) => void;
    children?: ReactNode;
    deliveryVo?: DeliveryVo;
    onRefresh?: () => void | Promise<void>;
    deliveryEditor?: ReactNode;
}

/** 提货详情组合组件，按旧版页签切换真实提货、收款、凭证和物流内容。 */
export default function DeliveryDetail({
    data = {},
    payHistory = [],
    tracking = [],
    materials = [],
    showPayHistory = true,
    headerContent,
    onTabChange,
    onEdit,
    onCancel,
    onInitiate,
    onDelete,
    children,
    deliveryVo,
    onRefresh,
    deliveryEditor
}: DeliveryDetailProps) {
    const [activeTab, setActiveTab] = useState("delivery");
    const tabItems = [
        ["delivery", "提货信息"],
        ["payment", "收款记录"],
        ["material", "凭证材料"],
        ["tracking", "物流跟踪"]
    ].filter(([key]) => showPayHistory || key !== "payment");
    const selectTab = (tab: string) => {
        setActiveTab(tab);
        onTabChange?.(tab);
        document.getElementById(`trade-delivery-${tab === "delivery" ? "message" : tab}`)?.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    };
    const tabs = (
        <div className="trade-delivery-detail-tabs">
            {tabItems.map(([key, label]) => (
                <button
                    key={key}
                    type="button"
                    className={activeTab === key ? "is-active" : ""}
                    onClick={() => selectTab(key)}
                >
                    {label}
                </button>
            ))}
        </div>
    );
    const content = (
        <>
            <div id="trade-delivery-message">
                {deliveryEditor || (
                    <DeliveryMessage
                        data={data}
                        deliveryVo={deliveryVo}
                        onCancel={onCancel}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        onInitiate={onInitiate}
                        onClickOrderDetail={orderId => {
                            if (orderId) window.location.href = `/tradeManage/tradeOrder/detail?id=${String(orderId)}`;
                        }}
                    />
                )}
            </div>
            {showPayHistory ? (
                <div id="trade-delivery-payment">
                    <PayHistory items={payHistory} />
                </div>
            ) : null}
            <div id="trade-delivery-material">
                <ProveMaterial deliveryVo={deliveryVo} items={materials} onSaved={onRefresh} />
            </div>
            <div id="trade-delivery-tracking">
                <LogisticsTracking deliveryVo={deliveryVo} items={tracking} />
            </div>
            {children}
        </>
    );
    if (headerContent) {
        return (
            <>
                <section className="section-card mb-2">
                    {headerContent}
                    {tabs}
                </section>
                <div className="trade-delivery-detail-content">{content}</div>
            </>
        );
    }
    return (
        <section className="section-card mb-4">
            <div className="section-title">提货详情</div>
            {tabs}
            {content}
        </section>
    );
}
